import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';

import FunctionNode    from './nodes/FunctionNode';
import HumanNode       from './nodes/HumanNode';
import GateNode        from './nodes/GateNode';
import ConnectionNode  from './nodes/ConnectionNode';
import NoteNode        from './nodes/NoteNode';
import CollectionNode  from './nodes/CollectionNode';
import TriggerNode     from './nodes/TriggerNode';
import ConditionNode   from './nodes/ConditionNode';
import ActionNode      from './nodes/ActionNode';
import { evaluateCondition, saveWorkflowState, loadWorkflowState, executeAction, addWorkflowStep, completeWorkflowStep, failWorkflowStep } from '../../utils/workflowEngine';
import NodePanel        from './NodePanel';
import ContextMenu      from './ContextMenu';
import CollectionModal  from './CollectionModal';
import FunctionTaskView from './FunctionTaskView';
import { ChatModal, TasksModal } from './PipelineModals';
import GeneratorModal   from './GeneratorModal';
import ExecutionPanel, { normaliseSchedule } from './ExecutionPanel';
import { PipelineCtx } from './PipelineContext';
import { dataService } from '../../services/dataService';
import { addInboxItem } from '../../utils/inboxStorage';
import { addLog } from '../../utils/logsStorage';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { useWorkspaceRole } from '../../contexts/WorkspaceRoleContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';

const callResolveGate = httpsCallable(functions, 'resolveGate');
const callRunManualTrigger = httpsCallable(functions, 'runManualTrigger');

function loadExecConfig(workspaceId) {
  if (!workspaceId) return null;
  try { return JSON.parse(localStorage.getItem(`pipelineExecution_${workspaceId}`)); } catch { return null; }
}
function saveExecConfig(workspaceId, config) {
  if (!workspaceId) return;
  localStorage.setItem(`pipelineExecution_${workspaceId}`, JSON.stringify(config));
}

const NODE_TYPES = {
  functionNode:    FunctionNode,
  humanNode:       HumanNode,
  gateNode:        GateNode,
  connectionNode:  ConnectionNode,
  noteNode:        NoteNode,
  collectionNode:  CollectionNode,
  triggerNode:     TriggerNode,
  conditionNode:   ConditionNode,
  actionNode:      ActionNode,
};

const EDGE_DEFAULTS = {
  type: 'default',
  animated: true,
  markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
  style: { stroke: '#94a3b8', strokeWidth: 2 }
};

const NODE_COLORS = [
  { name: 'Indigo',  value: '#6366f1' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Amber',   value: '#f59e0b' },
  { name: 'Rose',    value: '#f43f5e' },
  { name: 'Cyan',    value: '#0ea5e9' },
  { name: 'Violet',  value: '#8b5cf6' },
  { name: 'Orange',  value: '#f97316' },
  { name: 'Teal',    value: '#0d9488' },
  { name: 'Slate',   value: '#64748b' },
];

const NOTE_COLORS = [
  { name: 'Yellow',   value: '#fef9c3' },
  { name: 'Blue',     value: '#dbeafe' },
  { name: 'Green',    value: '#dcfce7' },
  { name: 'Pink',     value: '#fce7f3' },
  { name: 'Lavender', value: '#ede9fe' },
  { name: 'Peach',    value: '#ffedd5' },
];

const DEMO_NODES = [
  {
    id: 'node-1',
    type: 'humanNode',
    position: { x: 60, y: 240 },
    data: { label: 'Business Owner', description: 'Raises requests, approves content and major decisions.' }
  },
  {
    id: 'node-2',
    type: 'functionNode',
    position: { x: 340, y: 80 },
    data: {
      label: 'Marketing',
      description: 'Handles social media, content creation, and brand campaigns.',
      agents: [
        { id: 'a1', name: 'Alex', role: 'Marketing Manager',       isManager: true,  model: 'standard' },
        { id: 'a2', name: 'Aria', role: 'Social media & scheduling', isManager: false, model: 'fast' },
        { id: 'a3', name: 'Liam', role: 'Copywriting & captions',  isManager: false, model: 'standard' },
      ]
    }
  },
  {
    id: 'node-3',
    type: 'gateNode',
    position: { x: 660, y: 80 },
    data: { label: 'Content Approval', description: 'All outgoing posts require approval before publishing.' }
  },
  {
    id: 'node-4',
    type: 'connectionNode',
    position: { x: 940, y: 80 },
    data: { label: 'Instagram / Canva', description: 'Post to Instagram via Canva-generated assets.' }
  },
  {
    id: 'node-5',
    type: 'functionNode',
    position: { x: 340, y: 340 },
    data: {
      label: 'Service Desk',
      description: 'Handles incoming support tickets, triages and escalates.',
      agents: [
        { id: 'a4', name: 'James', role: 'Service Desk Manager', isManager: true,  model: 'standard' },
        { id: 'a5', name: 'Dana',  role: 'Tier-1 support',       isManager: false, model: 'fast' },
        { id: 'a6', name: 'Riley', role: 'Tier-1 support',       isManager: false, model: 'fast' },
      ]
    }
  },
  {
    id: 'node-6',
    type: 'functionNode',
    position: { x: 660, y: 340 },
    data: {
      label: 'Development',
      description: 'Receives escalated bugs and feature requests.',
      agents: [
        { id: 'a7', name: 'Sarah',  role: 'Dev Manager',         isManager: true,  model: 'smart' },
        { id: 'a8', name: 'Kai',    role: 'Frontend specialist', isManager: false, model: 'standard' },
        { id: 'a9', name: 'Morgan', role: 'Backend specialist',  isManager: false, model: 'smart' },
      ]
    }
  },
  {
    id: 'node-7',
    type: 'functionNode',
    position: { x: 940, y: 340 },
    data: {
      label: 'Project Assistant',
      description: 'Background AI monitoring all pipeline activity. Handles external comms including email.',
      color: '#0ea5e9',
      agents: [
        { id: 'a10', name: 'Atlas', role: 'Pipeline monitor & external comms', isManager: true, model: 'smart' }
      ]
    }
  },
  {
    id: 'col-1',
    type: 'collectionNode',
    position: { x: 340, y: 580 },
    data: {
      label: 'Service Desk Tickets',
      description: 'Support tickets raised by users',
      fields: [
        { id: 'f1', name: 'Description',  type: 'text' },
        { id: 'f2', name: 'Assigned To',  type: 'agent' },
        { id: 'f3', name: 'Status',       type: 'select', options: ['Open', 'In Progress', 'Resolved', 'Escalated'] },
        { id: 'f4', name: 'Priority',     type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
      ],
      records: [
        { id: 'r1', values: { f1: 'Button too small on mobile login',    f2: 'Riley', f3: 'Open',        f4: 'High'     } },
        { id: 'r2', values: { f1: 'Password reset not working',          f2: 'Dana',  f3: 'In Progress', f4: 'Critical' } },
        { id: 'r3', values: { f1: 'Dashboard loading slow (>5s)',        f2: 'James', f3: 'Open',        f4: 'Medium'   } },
        { id: 'r4', values: { f1: 'CSS misalignment on settings page',   f2: null,    f3: 'Escalated',   f4: 'High'     } },
        { id: 'r5', values: { f1: 'Dark mode toggle not saving',         f2: 'Riley', f3: 'Resolved',    f4: 'Low'      } },
      ]
    }
  },
  {
    id: 'col-2',
    type: 'collectionNode',
    position: { x: 660, y: 580 },
    data: {
      label: 'Dev Tickets',
      description: 'Escalated bugs and development tasks',
      fields: [
        { id: 'f1', name: 'Task',          type: 'text' },
        { id: 'f2', name: 'Developer',     type: 'agent' },
        { id: 'f3', name: 'Status',        type: 'select', options: ['Backlog', 'In Progress', 'Review', 'Done'] },
        { id: 'f4', name: 'Type',          type: 'select', options: ['Bug', 'Feature', 'Escalation'] },
        { id: 'f5', name: 'Source Ticket', type: 'reference' },
      ],
      records: [
        { id: 'r1', values: { f1: 'Fix mobile button sizing',         f2: 'Kai',    f3: 'In Progress', f4: 'Escalation', f5: 'SD-r4' } },
        { id: 'r2', values: { f1: 'Auth service overhaul',            f2: 'Morgan', f3: 'Backlog',     f4: 'Bug'        } },
        { id: 'r3', values: { f1: 'Performance optimisation for API', f2: 'Morgan', f3: 'In Progress', f4: 'Feature'    } },
      ]
    }
  },
  {
    id: 'note-1',
    type: 'noteNode',
    position: { x: 80, y: 540 },
    data: {
      title: 'Button Size Escalation',
      body: 'If a user raises an issue about button sizing or UI layout bugs, escalate directly to Developers. Do not attempt to resolve at tier-1.',
      color: '#fef9c3',
      assignedLabels: ['Service Desk', 'Development']
    }
  },
  {
    id: 'note-2',
    type: 'noteNode',
    position: { x: 940, y: 580 },
    data: {
      title: 'Email Protocol',
      body: 'When a dev ticket is resolved, Atlas (Project Assistant) should send a summary email to the admin. Do not rely on agent access for external comms.',
      color: '#dbeafe',
      assignedLabels: ['Development', 'Project Assistant']
    }
  },
];

const DEMO_EDGES = [
  { id: 'e1-2',     source: 'node-1', target: 'node-2', ...EDGE_DEFAULTS },
  { id: 'e2-3',     source: 'node-2', target: 'node-3', ...EDGE_DEFAULTS },
  { id: 'e3-4',     source: 'node-3', target: 'node-4', ...EDGE_DEFAULTS },
  { id: 'e1-5',     source: 'node-1', target: 'node-5', ...EDGE_DEFAULTS },
  { id: 'e5-6',     source: 'node-5', target: 'node-6', ...EDGE_DEFAULTS },
  { id: 'e6-7',     source: 'node-6', target: 'node-7', ...EDGE_DEFAULTS },
  { id: 'e5-col1',  source: 'node-5', target: 'col-1',  ...EDGE_DEFAULTS },
  { id: 'e6-col2',  source: 'node-6', target: 'col-2',  ...EDGE_DEFAULTS },
];

let nodeIdCounter = 100;
const newId = (prefix = 'node') => `${prefix}-${++nodeIdCounter}`;

const fmtTokens = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
// Rough cost estimate from tokens (assumes ~50/50 input/output, Sonnet pricing)
const fmtCost = (tokens) => {
  const usd = (tokens * 0.5 / 1_000_000) * 3 + (tokens * 0.5 / 1_000_000) * 15;
  return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(3)}`;
};

function buildMockTokens(nodeList) {
  const data = {};
  nodeList.forEach(node => {
    if (node.type === 'functionNode') {
      const agentMap = {};
      let total = 0;
      (node.data.agents || []).forEach(a => {
        const t = Math.floor(Math.random() * 9000 + 400);
        agentMap[a.id] = t;
        total += t;
      });
      data[node.id] = { total, agents: agentMap };
    } else if (['gateNode', 'collectionNode', 'connectionNode'].includes(node.type)) {
      data[node.id] = { total: Math.floor(Math.random() * 2200 + 80) };
    }
  });
  return data;
}

export default function PipelineCanvas({ workspaceId, opsMode = false }) {
  const { aiEnabled } = useFeatureFlags();
  const { hasCapability } = useWorkspaceRole();
  const canEdit = hasCapability('edit_pipeline');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode]   = useState(null);
  const [contextMenu, setContextMenu]     = useState(null);
  const [showTokens, setShowTokens]       = useState(false);
  const [tokenData, setTokenData]         = useState({});
  const [pipelineActive,   setPipelineActive]   = useState(false);
  const [pipelineSchedule, setPipelineSchedule] = useState({ type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' });
  const [showExecution,    setShowExecution]    = useState(false);
  const [loading, setLoading]             = useState(true);
  const [showStarter, setShowStarter]     = useState(false);
  const [saveState, setSaveState]         = useState('saved');
  const [walkingAvatars, setWalkingAvatars] = useState([]);
  const [flowDots, setFlowDots] = useState([]);
  const [chatWindows, setChatWindows]     = useState([]);
  const [genWindows,  setGenWindows]      = useState([]);
  const [tasksModal, setTasksModal]       = useState(null);
  const [functionTaskView, setFunctionTaskView] = useState(null);
  const [collectionModal, setCollectionModal] = useState(null);
  const [recordsByCol, setRecordsByCol]   = useState({}); // collectionId → records[]
  const recordsByColRef    = useRef({});   // synchronous mirror for save/registry builders
  const saveTimer          = useRef(null);
  const rfInstance         = useRef(null);
  const pendingChatRef     = useRef(null);
  const prevNodesRef       = useRef([]);   // for record_added / field_change trigger detection
  const hadNodesRef        = useRef(false); // canvas held real nodes this session (guards empty saves)
  const unmountedRef       = useRef(false); // an unmounted canvas must never write the pipeline
  const triggerCooldownRef = useRef({});   // triggerNodeId → last-fired timestamp (ms)
  const workflowQueueRef   = useRef([]);
  const workflowRunningRef = useRef(false);
  // Always-current refs so memoized callbacks never walk stale state
  const nodesRef           = useRef([]);
  const edgesRef           = useRef([]);

  // A pending debounced save dies with the canvas: React Flow can dispatch
  // node-remove changes during teardown, so a save that fires after the user
  // navigated away could persist an emptied/stale canvas over writes made
  // elsewhere (e.g. a table just created in Data).
  useEffect(() => () => {
    unmountedRef.current = true;
    clearTimeout(saveTimer.current);
  }, []);

  // Check for pending chat-open request from ChatsPage
  useEffect(() => {
    const p = localStorage.getItem('projectly_open_chat');
    if (p) { localStorage.removeItem('projectly_open_chat'); pendingChatRef.current = p; }
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    const saved = loadExecConfig(workspaceId);
    if (saved) {
      if (saved.scheduleConfig !== undefined) setPipelineSchedule(normaliseSchedule(saved.scheduleConfig) || { type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    saveExecConfig(workspaceId, { active: pipelineActive, scheduleConfig: pipelineSchedule });
  }, [workspaceId, pipelineActive, pipelineSchedule]);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    dataService.getPipeline(workspaceId).then(data => {
      if (data.nodes && data.nodes.length > 0) {
        const normalizedEdges = (data.edges || []).map(e => ({
          ...EDGE_DEFAULTS,
          ...e,
          style: { ...EDGE_DEFAULTS.style, ...(e.style || {}) },
          markerEnd: e.markerEnd || EDGE_DEFAULTS.markerEnd,
        }));
        setNodes(data.nodes);
        setEdges(normalizedEdges);
        setTokenData(buildMockTokens(data.nodes));
        setShowStarter(false);
        hadNodesRef.current = true;
        if (data.active !== undefined) setPipelineActive(data.active);
        prevNodesRef.current = data.nodes;
        nodesRef.current     = data.nodes;
        edgesRef.current     = normalizedEdges;
      } else {
        setNodes([]);
        setEdges([]);
        setShowStarter(true);
        prevNodesRef.current = [];
        nodesRef.current     = [];
        edgesRef.current     = [];
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Live sync: reflect server-side workflow writes (records, tasks, gate
  // approvals) on the open canvas without clobbering local layout/edits. Only
  // applies writes stamped by the workflow engine; the browser's own saves are
  // ignored here since local state is already current.
  useEffect(() => {
    if (!workspaceId) return;
    const unsub = dataService.subscribePipeline(workspaceId, (data) => {
      if (data._lastModifiedBy !== 'workflow_engine') return;
      const serverById = Object.fromEntries((data.nodes || []).map(n => [n.id, n]));
      setNodes(local => {
        const merged = local.map(ln => {
          const sn = serverById[ln.id];
          if (!sn) return ln;
          // Records now live in the per-collection subcollection (see dataService),
          // so they are no longer mirrored from the pipeline doc here.
          if (ln.type === 'functionNode') {
            return { ...ln, data: { ...ln.data, tasks: sn.data?.tasks ?? ln.data.tasks } };
          }
          if (ln.type === 'gateNode') {
            return { ...ln, data: { ...ln.data,
              pendingApprovals:   sn.data?.pendingApprovals   ?? ln.data.pendingApprovals,
              completedApprovals: sn.data?.completedApprovals ?? ln.data.completedApprovals } };
          }
          return ln;
        });
        // Keep refs in sync so trigger detection diffs against the server state
        // (prevents the browser from re-triggering on the server's own writes).
        nodesRef.current     = merged;
        prevNodesRef.current = merged;
        return merged;
      });
    });
    return () => unsub && unsub();
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open pending chat after pipeline data loads
  useEffect(() => {
    if (loading || !pendingChatRef.current) return;
    const pending = pendingChatRef.current;
    pendingChatRef.current = null;
    const m = pending.match(/^(.+?)_(agent|team|direct)_(.*)$/);
    if (!m) return;
    const [, nodeId, mode, agentName] = m;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const agent = agentName ? (node.data.agents || []).find(a => a.name === agentName) : null;
    openChat({
      nodeId,
      mode,
      agentName:         agent?.name,
      agentRole:         agent?.role,
      agentInstructions: agent?.instructions,
      nodeLabel:         node.data.label,
      playbook:          node.data.playbook,
      connectedCollections: getConnectedCollections(nodeId),
      connectedGates: getConnectedGates(nodeId),
      teamMembers: (node.data.agents || [])
        .filter(a => !agentName || a.name !== agentName)
        .map(a => ({ name: a.name, role: a.role, isManager: a.isManager })),
    });
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to every collection node's records so synchronous consumers
  // (agent chat context, the InboxView function registry) always see live
  // subcollection data instead of the now-removed embedded node.data.records.
  const collectionIdsKey = nodes
    .filter(n => n.type === 'collectionNode')
    .map(n => n.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!workspaceId) return;
    const colIds = collectionIdsKey ? collectionIdsKey.split(',') : [];
    const unsubs = colIds.map(colId =>
      dataService.subscribeRecords(workspaceId, colId, (recs) => {
        setRecordsByCol(prev => {
          const next = { ...prev, [colId]: recs };
          recordsByColRef.current = next;
          return next;
        });
      })
    );
    return () => unsubs.forEach(u => u && u());
  }, [workspaceId, collectionIdsKey]);

  const handleStarterChoice = async (useTemplate) => {
    const n = useTemplate ? DEMO_NODES : [];
    const e = useTemplate ? DEMO_EDGES : [];
    setTokenData(buildMockTokens(n));
    setShowStarter(false);
    // deployPipeline strips embedded sample records and seeds them into each
    // collection's subcollection, returning the cleaned nodes to render.
    // Activate the starter template so its triggers fire immediately; an empty
    // "start from scratch" pipeline stays inactive until the user turns it on.
    const cleanNodes = await dataService.deployPipeline(workspaceId, n, e, useTemplate);
    if (useTemplate) setPipelineActive(true);
    setNodes(cleanNodes);
    setEdges(e);
  };

  const scheduleSave = useCallback((nextNodes, nextEdges) => {
    if (!workspaceId || !canEdit) return;
    // Never persist an EMPTY pipeline unless this canvas actually held nodes
    // this session (a deliberate delete-all). Without this guard, mount-time
    // React Flow change events on an empty/starter canvas schedule a debounced
    // empty save that can fire AFTER the user navigates away — clobbering a
    // collection they just created in Tables (single doc, multiple writers).
    if (nextNodes.length === 0 && !hadNodesRef.current) return;
    if (nextNodes.length > 0) hadNodesRef.current = true;
    // Keep refs in sync on every mutation so runWorkflow/checkTriggers always see live state
    nodesRef.current = nextNodes;
    edgesRef.current = nextEdges;
    setSaveState('saving');
    clearTimeout(saveTimer.current);
    // Keep a function + agent registry in localStorage so InboxView compose can read available agents
    try {
      const fnRegistry = nextNodes
        .filter(n => n.type === 'functionNode')
        .map(n => {
          // Inline connected-collection lookup so we don't need getConnectedCollections callback in deps
          const connectedIds = new Set(
            nextEdges
              .filter(e => e.source === n.id || e.target === n.id)
              .map(e => e.source === n.id ? e.target : e.source)
          );
          const collections = nextNodes
            .filter(cn => connectedIds.has(cn.id) && cn.type === 'collectionNode')
            .map(cn => ({
              id: cn.id,
              label: cn.data.label || 'Unnamed',
              fields: (cn.data.fields || []).map(f => f.name || f.label || f),
              records: (recordsByColRef.current[cn.id] || []).slice(0, 100),
            }));
          return {
            id: n.id,
            label: n.data.label || 'Function',
            playbook: n.data.playbook || '',
            collections,
            agents: (n.data.agents || []).map(a => ({ name: a.name, role: a.role, isManager: a.isManager })),
          };
        });
      localStorage.setItem(`projectly_fn_registry_${workspaceId}`, JSON.stringify(fnRegistry));
      // Flat agent list grouped by function for compose dropdown
      const agentRegistry = [];
      fnRegistry.forEach(fn => {
        (fn.agents || []).forEach(a => {
          agentRegistry.push({
            id: `${fn.id}__${a.name}`,
            name: a.name,
            role: a.role || '',
            isManager: !!a.isManager,
            functionId: fn.id,
            functionLabel: fn.label,
            playbook: fn.playbook,
            collections: fn.collections || [],
          });
        });
      });
      localStorage.setItem(`projectly_agent_registry_${workspaceId}`, JSON.stringify(agentRegistry));
    } catch {}
    saveTimer.current = setTimeout(async () => {
      if (unmountedRef.current) return; // belt-and-braces with the unmount cleanup
      await dataService.savePipeline(workspaceId, nextNodes, nextEdges);
      setSaveState('saved');
    }, 1200);
  }, [workspaceId, canEdit]);

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setNodes(nds => { scheduleSave(nds, edges); return nds; });
  }, [onNodesChange, edges, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setEdges(eds => { scheduleSave(nodes, eds); return eds; });
  }, [onEdgesChange, nodes, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConnect = useCallback((params) => {
    const sourceNode = nodes.find(n => n.id === params.source);
    let extra = {};
    if (sourceNode?.type === 'conditionNode') {
      const isYes = params.sourceHandle === 'yes';
      extra = {
        label: isYes ? 'Yes' : 'No',
        labelStyle: { fill: isYes ? '#10b981' : '#f43f5e', fontWeight: 700, fontSize: 10 },
        labelBgStyle: { fill: 'var(--bg-primary)', fillOpacity: 0.85, rx: 3 },
        style: { stroke: isYes ? '#10b981' : '#f43f5e', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isYes ? '#10b981' : '#f43f5e' },
      };
    }
    const newEdge = { ...params, ...EDGE_DEFAULTS, ...extra, id: `e-${Date.now()}` };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setEdges(eds => {
      const next = addEdge(newEdge, eds);
      scheduleSave(nodes, next);
      return next;
    });
  }, [nodes, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNodeClick = useCallback((_, node) => {
    if (node.type === 'collectionNode') setCollectionModal(node.id);
    else setSelectedNode(node);
  }, []);
  const onPaneClick      = useCallback(() => { setSelectedNode(null); setContextMenu(null); }, []);

  const updateNodeData = useCallback((nodeId, updates) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setNodes(nds => {
      const prev = prevNodesRef.current;
      const next = nds.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
      );
      scheduleSave(next, edgesRef.current);
      setSelectedNode(p =>
        p?.id === nodeId ? { ...p, data: { ...p.data, ...updates } } : p
      );
      setTimeout(() => checkTriggers(prev, next), 0);
      prevNodesRef.current = next;
      return next;
    });
  }, [scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const getConnectedCollections = useCallback((nodeId) => {
    const connectedIds = new Set(
      edges
        .filter(e => e.source === nodeId || e.target === nodeId)
        .map(e => e.source === nodeId ? e.target : e.source)
    );
    return nodes
      .filter(n => connectedIds.has(n.id) && n.type === 'collectionNode')
      .map(n => ({ id: n.id, label: n.data.label || 'Unnamed', fields: n.data.fields || [], records: recordsByCol[n.id] || [] }));
  }, [nodes, edges, recordsByCol]);

  const getConnectedGates = useCallback((nodeId) => {
    const connectedIds = new Set(
      edges
        .filter(e => e.source === nodeId || e.target === nodeId)
        .map(e => e.source === nodeId ? e.target : e.source)
    );
    return nodes
      .filter(n => connectedIds.has(n.id) && n.type === 'gateNode')
      .map(n => ({ id: n.id, label: n.data.label || 'Approval Gate', approver: n.data.approver, criteria: n.data.criteria }));
  }, [nodes, edges]);

  const handleGateRequest = useCallback((gateId, approval) => {
    let targetId = gateId;
    if (!targetId && approval.chatKey) {
      const srcNodeId = approval.chatKey.split('_')[0];
      const connIds = edges
        .filter(e => e.source === srcNodeId || e.target === srcNodeId)
        .map(e => e.source === srcNodeId ? e.target : e.source);
      targetId = connIds.find(id => nodes.find(n => n.id === id && n.type === 'gateNode')) || null;
    }
    if (!targetId) return;
    const newApproval = { id: `ga_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`, ...approval, requestedAt: new Date().toISOString() };
    const gateNode = nodes.find(n => n.id === targetId);
    updateNodeData(targetId, { pendingApprovals: [...(gateNode?.data?.pendingApprovals || []), newApproval] });
    addInboxItem({
      id: `inbox_${newApproval.id}`,
      type: 'approval',
      subject: `Approve: ${approval.action}`,
      from: approval.agentName || approval.nodeLabel || 'Agent',
      fromNode: approval.nodeLabel || '',
      preview: approval.description || approval.action,
      body: `**Action requested:** ${approval.action}${approval.description ? `\n\n${approval.description}` : ''}\n\n**Requested by:** ${approval.agentName || 'Agent'} (${approval.nodeLabel || 'Pipeline'})\n**Gate:** ${approval.gateLabel || 'Approval Gate'}\n**Time:** ${new Date().toLocaleString()}`,
      timestamp: new Date().toISOString(),
      read: false, starred: false, folder: 'inbox',
      approvalId: newApproval.id, gateId: targetId,
      chatKey: approval.chatKey, sessionId: approval.sessionId,
      // stored explicitly so InboxView can construct the response without needing nodes
      action: approval.action,
      gateLabel: approval.gateLabel || 'Approval Gate',
      decision: null,
      workspaceId: workspaceId || null,
    });
    addLog({ type: 'gate_request', nodeLabel: approval.nodeLabel || 'Pipeline', agentName: approval.agentName, detail: `Approval requested: ${approval.action}` });
  }, [nodes, edges, updateNodeData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Workflow execution engine ──────────────────────────────────────────────
  // Reads from nodesRef/edgesRef so it always walks the live graph regardless of
  // when it was memoized. Stable dep array means it's safe to call from trigger
  // detection without causing re-renders.
  const runWorkflow = useCallback(async (triggerNodeId, resumeFromNodeId = null, existingWorkflowId = null, recordId = null) => {
    if (!resumeFromNodeId) {
      const now = Date.now();
      const lastFired = triggerCooldownRef.current[triggerNodeId] || 0;
      if (now - lastFired < 3000) return;
      triggerCooldownRef.current[triggerNodeId] = now;
    }
    const liveNodes   = nodesRef.current;
    const triggerNode = liveNodes.find(n => n.id === triggerNodeId);
    // Server is the single executor for ALL trigger types. The browser walks the
    // graph for live dot animation only — it skips real side effects to avoid
    // duplicate writes/notifications. Gate resumes use resolveGate callable.
    const animateOnly = !resumeFromNodeId;

    if (!resumeFromNodeId && triggerNode?.data?.triggerType === 'manual') {
      callRunManualTrigger({ workspaceId, triggerNodeId })
        .catch(e => console.error('[runManualTrigger]', e));
    }
    const workflowId  = existingWorkflowId || `wf_${Date.now()}`;
    const baseState   = {
      id: workflowId, workspaceId, triggerNodeId,
      triggerLabel: triggerNode?.data?.label || 'Trigger',
      recordId: recordId || null,
      status: 'running',
      startedAt: new Date().toISOString(),
      steps: [],
    };
    saveWorkflowState(baseState);
    addLog({ type: 'workflow', nodeLabel: 'Pipeline', agentName: 'System', detail: `Workflow ${workflowId} started` });

    if (!resumeFromNodeId) {
      const liveEdges = edgesRef.current;
      const incomingEdge = liveEdges.find(e => e.target === triggerNodeId);
      if (incomingEdge) {
        await animateFlowDot(incomingEdge.source, triggerNodeId);
      }
    }

    const stepThrough = async (nodeId) => {
      // Always re-read refs so that mid-workflow saves (e.g. action node writing to
      // Firestore then updating state) are reflected in subsequent steps.
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const node = currentNodes.find(n => n.id === nodeId);
      if (!node) return;
      const out = currentEdges.filter(e => e.source === nodeId);

      addWorkflowStep(workflowId, { nodeId, nodeType: node.type, nodeLabel: node.data?.label || node.type, status: 'running' });

      // noteNode is visual-only for now; AI agents will read connected notes as contextual instructions.
      if (node.type === 'triggerNode' || node.type === 'humanNode' || node.type === 'collectionNode' || node.type === 'connectionNode' || node.type === 'noteNode') {
        completeWorkflowStep(workflowId, nodeId, 'Pass-through');
        if (out[0]) { await animateFlowDot(nodeId, out[0].target); await stepThrough(out[0].target); }

      } else if (node.type === 'conditionNode') {
        const pass     = await evaluateCondition(node.data?.conditions, node.data?.logic, currentNodes, recordId, dataService, workspaceId);
        const nextEdge = out.find(e => e.sourceHandle === (pass ? 'yes' : 'no')) || out[pass ? 0 : 1] || out[0];
        const detail   = `Condition → ${pass ? 'YES' : 'NO'}`;
        completeWorkflowStep(workflowId, nodeId, detail);
        addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Condition', agentName: 'System', detail });
        if (nextEdge) { await animateFlowDot(nodeId, nextEdge.target); await stepThrough(nextEdge.target); }

      } else if (node.type === 'actionNode') {
        if (animateOnly) {
          // Server performs the real side effect; browser only shows the step.
          completeWorkflowStep(workflowId, nodeId, `Executed on server: ${node.data?.actionType || 'update_field'}`);
          addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Action', agentName: 'System', detail: 'Action runs on server' });
        } else {
          try {
            await executeAction(node, currentNodes, dataService, workspaceId, addInboxItem, recordId, currentEdges);
            const detail = `Action executed: ${node.data?.actionType || 'update_field'}`;
            completeWorkflowStep(workflowId, nodeId, detail);
            addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Action', agentName: 'System', detail });
          } catch (e) {
            failWorkflowStep(workflowId, nodeId, String(e));
            return;
          }
        }
        for (const edge of out) {
          const targetNode = currentNodes.find(n => n.id === edge.target);
          await animateFlowDot(nodeId, edge.target);
          if (targetNode?.type !== 'collectionNode') {
            await stepThrough(edge.target);
          }
        }

      } else if (node.type === 'functionNode') {
        if (animateOnly) {
          // Server creates the task; browser only shows the step.
          completeWorkflowStep(workflowId, nodeId, 'Task created on server');
          addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Function', agentName: 'System', detail: 'Task created on server' });
        } else if (aiEnabled) {
          const prefill = `[Workflow task] ${node.data?.description || node.data?.label || 'Process this workflow step.'}`;
          openChat({
            nodeId: node.id, nodeLabel: node.data?.label, playbook: node.data?.playbook,
            mode: 'team',
            connectedCollections: getConnectedCollections(node.id),
            connectedGates: getConnectedGates(node.id),
            teamMembers: (node.data?.agents || []).map(a => ({ name: a.name, role: a.role, isManager: a.isManager })),
            prefillMessage: prefill,
          });
          completeWorkflowStep(workflowId, nodeId, 'Agent notified');
          addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Function', agentName: 'System', detail: 'Agent chat opened' });
        } else {
          const taskFields = node.data?.taskFields || [];
          const titleField = taskFields.find(f => f.type === 'text');
          const statusField = taskFields.find(f => f.name?.toLowerCase() === 'status');
          const newTask = {
            id: `t${Date.now()}`,
            values: {},
            createdAt: new Date().toISOString(),
            sourceRef: { workflowId },
          };
          if (titleField) newTask.values[titleField.id] = `[Workflow] ${node.data?.description || node.data?.label || 'Process this step'}`;
          if (statusField) newTask.values[statusField.id] = statusField.default || (statusField.options || [])[0] || 'To do';
          taskFields.forEach(f => { if (f.default && !newTask.values[f.id]) newTask.values[f.id] = f.default; });
          const existingTasks = node.data?.tasks || [];
          updateNodeData(node.id, { tasks: [...existingTasks, newTask] });
          completeWorkflowStep(workflowId, nodeId, 'Task created in queue');
          addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Function', agentName: 'System', detail: 'Task added to human queue' });
          addInboxItem({
            id: `inbox_wf_task_${Date.now()}`,
            type: 'notification',
            subject: `New task in ${node.data?.label || 'Function'}`,
            from: 'Workflow', fromNode: 'Pipeline',
            preview: newTask.values[titleField?.id] || 'A new workflow task requires attention',
            body: `A workflow has created a new task in "${node.data?.label || 'Function'}". Please review the task queue.`,
            timestamp: new Date().toISOString(),
            read: false, starred: false, folder: 'inbox',
            workspaceId: workspaceId || null,
          });
        }
        if (out[0]) setTimeout(async () => { await animateFlowDot(nodeId, out[0].target); stepThrough(out[0].target); }, 400);

      } else if (node.type === 'gateNode') {
        const resumeNodeId = out[0]?.target || null;
        if (animateOnly) {
          // Server owns this gate: it creates the approval (which appears here via
          // live sync) and resumes on approval. The browser just shows the pause.
          completeWorkflowStep(workflowId, nodeId, 'Awaiting approval (server)');
          addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Gate', agentName: 'System', detail: 'Paused at gate — awaiting approval (server)' });
          return;
        }
        saveWorkflowState({
          ...loadWorkflowState(workflowId),
          status: 'paused', pausedNodeId: nodeId, resumeNodeId,
          pausedAt: new Date().toISOString(),
          pauseReason: node.data?.label || 'Gate approval required',
        });
        handleGateRequest(nodeId, {
          action: node.data?.label || 'Gate approval required',
          description: node.data?.criteria || '',
          gateLabel: node.data?.label || 'Approval Gate',
          agentName: 'Workflow', nodeLabel: 'Workflow',
          workflowId, triggerNodeId, resumeNodeId,
        });
        completeWorkflowStep(workflowId, nodeId, 'Awaiting approval');
        addLog({ type: 'workflow', nodeLabel: node.data?.label || 'Gate', agentName: 'System', detail: 'Workflow paused — awaiting gate approval' });
        return; // don't fall through to 'completed'
      }
    };

    await stepThrough(resumeFromNodeId || triggerNodeId);

    // Only mark completed if not paused
    const wfState = loadWorkflowState(workflowId);
    if (wfState?.status !== 'paused') {
      saveWorkflowState({ ...wfState, status: 'completed', completedAt: new Date().toISOString() });
      addLog({ type: 'workflow', nodeLabel: 'Pipeline', agentName: 'System', detail: `Workflow ${workflowId} completed` });
    }
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const queueWorkflow = useCallback((triggerNodeId, recordId) => {
    workflowQueueRef.current.push({ triggerNodeId, recordId });
    if (workflowRunningRef.current) return;
    const processQueue = async () => {
      workflowRunningRef.current = true;
      while (workflowQueueRef.current.length > 0) {
        const { triggerNodeId: tid, recordId: rid } = workflowQueueRef.current.shift();
        await runWorkflow(tid, null, null, rid);
        if (workflowQueueRef.current.length > 0) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }
      workflowRunningRef.current = false;
    };
    processQueue();
  }, [runWorkflow]);

  // ── Automatic trigger detection (function_task_change) ────────────────────
  // record_added / field_change triggers are detected server-side on the
  // records subcollection (see onRecordCreated / onRecordUpdated). Task triggers
  // still live in the pipeline doc, so they are detected here.
  const checkTriggers = useCallback((prevNodes, nextNodes) => {
    const triggerNodes = nextNodes.filter(n => n.type === 'triggerNode' && n.data.triggerType !== 'manual');
    if (!triggerNodes.length) return;

    const now = Date.now();
    const COOLDOWN_MS = 3000;

    triggerNodes.forEach(trigger => {
      const { triggerType } = trigger.data || {};
      const lastFired = triggerCooldownRef.current[trigger.id] || 0;
      if (now - lastFired < COOLDOWN_MS) return;

      if (triggerType === 'function_task_change') {
        const { triggerFunctionId, triggerTaskFieldId, triggerTaskValue } = trigger.data || {};
        if (!triggerFunctionId) return;
        const prevFn = prevNodes.find(n => n.id === triggerFunctionId);
        const nextFn = nextNodes.find(n => n.id === triggerFunctionId);
        if (!prevFn || !nextFn) return;
        const prevTasks = prevFn.data?.tasks || [];
        const nextTasks = nextFn.data?.tasks || [];
        const prevById = Object.fromEntries(prevTasks.map(t => [t.id, t]));

        // Check for new tasks
        const prevIds = new Set(prevTasks.map(t => t.id));
        const newTask = nextTasks.find(t => !prevIds.has(t.id));
        if (newTask && !triggerTaskFieldId) {
          addLog({ type: 'workflow', nodeLabel: trigger.data?.label || 'Trigger', agentName: 'System', detail: `function_task_change fired: new task ${newTask.id}` });
          queueWorkflow(trigger.id, null);
          return;
        }

        // Check for field value changes
        if (triggerTaskFieldId) {
          const changedTask = nextTasks.find(t => {
            const prev = prevById[t.id];
            if (!prev) return false;
            const newVal = String(t.values?.[triggerTaskFieldId] ?? '').toLowerCase().trim();
            const oldVal = String(prev.values?.[triggerTaskFieldId] ?? '').toLowerCase().trim();
            if (newVal === oldVal) return false;
            if (triggerTaskValue) return newVal === String(triggerTaskValue).toLowerCase().trim();
            return true;
          });
          if (changedTask) {
            addLog({ type: 'workflow', nodeLabel: trigger.data?.label || 'Trigger', agentName: 'System', detail: `function_task_change fired: task ${changedTask.id} field changed` });
            queueWorkflow(trigger.id, null);
          }
        }
      }
    });
  }, [queueWorkflow]);

  // skipChat = true when InboxView already wrote the chat response (avoids duplicate message)
  const handleGateApprove = useCallback((gateId, approvalId, decision, notes, skipChat = false) => {
    const gateNode = nodes.find(n => n.id === gateId);
    const approval = gateNode?.data?.pendingApprovals?.find(a => a.id === approvalId);
    if (approval && !skipChat) {
      const decisionEmoji = decision === 'approved' ? '✅' : '❌';
      const responseMsg = `${decisionEmoji} **Gate ${decision}** — ${approval.gateLabel || 'Approval Gate'}\n\n**Action:** ${approval.action}${notes ? `\n\n**Approver notes:** ${notes}` : ''}${decision === 'approved' ? '\n\nYou may proceed with this action.' : '\n\nPlease address the feedback and resubmit if needed.'}`;
      try {
        const raw = localStorage.getItem(`projectly_chat_${approval.chatKey}`);
        if (raw) {
          const chatData = JSON.parse(raw);
          const sess = chatData.sessions?.find(s => s.id === approval.sessionId);
          if (sess) {
            sess.messages.push({ id: Date.now(), role: 'assistant', text: responseMsg, suggestions: [] });
            localStorage.setItem(`projectly_chat_${approval.chatKey}`, JSON.stringify(chatData));
          }
        }
      } catch {}
      window.dispatchEvent(new CustomEvent('projectly_gate_response', {
        detail: { chatKey: approval.chatKey, sessionId: approval.sessionId, message: responseMsg }
      }));
    }
    const pendingApprovals = (gateNode?.data?.pendingApprovals || []).filter(a => a.id !== approvalId);
    const completedApprovals = [...(gateNode?.data?.completedApprovals || []).slice(-20), approval ? { ...approval, decision, notes, decidedAt: new Date().toISOString() } : null].filter(Boolean);
    updateNodeData(gateId, { pendingApprovals, completedApprovals });
    addLog({ type: 'gate_decision', nodeLabel: gateNode?.data?.label || 'Gate', agentName: 'User', detail: `${decision === 'approved' ? 'Approved' : 'Rejected'}: ${approval?.action || approvalId}` });

    // Resume the paused workflow. Server-created gates (data/schedule triggers)
    // resume on the server via resolveGate; browser-created gates (manual
    // triggers) resume locally. Server gates also get a reject call to clean up.
    if (approval?.source === 'server' && approval?.workflowId) {
      addLog({ type: 'workflow', nodeLabel: 'Pipeline', agentName: 'System', detail: `${decision === 'approved' ? 'Resuming' : 'Cancelling'} server workflow ${approval.workflowId}` });
      callResolveGate({ workspaceId, workflowId: approval.workflowId, decision })
        .catch(e => console.error('[resolveGate]', e));
    } else if (decision === 'approved' && approval?.workflowId && approval?.resumeNodeId) {
      const wfState = loadWorkflowState(approval.workflowId);
      if (wfState?.status === 'paused') {
        addLog({ type: 'workflow', nodeLabel: 'Pipeline', agentName: 'System', detail: `Resuming workflow ${approval.workflowId} after gate approval` });
        setTimeout(() => runWorkflow(approval.triggerNodeId, approval.resumeNodeId, approval.workflowId, wfState.recordId || null), 300);
      }
    }
  }, [nodes, updateNodeData, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process inbox approval decisions (from InboxView, including queued ones made while unmounted)
  useEffect(() => {
    const processPending = () => {
      try {
        const pending = JSON.parse(localStorage.getItem('projectly_pending_approvals') || '[]');
        if (pending.length === 0) return;
        // Only remove items we can actually process right now (gate node exists in loaded nodes)
        const remaining = [];
        for (const item of pending) {
          const { approvalId, gateId, decision, notes, chatHandled } = item;
          const gateNode = nodes.find(n => n.id === gateId);
          if (gateNode) {
            handleGateApprove(gateId, approvalId, decision, notes || '', chatHandled || false);
          } else {
            remaining.push(item); // nodes not loaded yet — retry on next effect run
          }
        }
        localStorage.setItem('projectly_pending_approvals', JSON.stringify(remaining));
      } catch {}
    };
    processPending();
    const handler = (e) => {
      const { approvalId, gateId, decision, notes, chatHandled } = e.detail;
      handleGateApprove(gateId, approvalId, decision, notes || '', chatHandled || false);
    };
    window.addEventListener('projectly_inbox_decision', handler);
    return () => window.removeEventListener('projectly_inbox_decision', handler);
  }, [handleGateApprove]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── InboxView bypass bridge ────────────────────────────────────────────────
  // InboxView's applyRecordOps writes directly to Firestore (bypassing setNodes),
  // so updateNodeData never fires and trigger detection is blind to those changes.
  // InboxView dispatches this event post-save; we diff against prevNodesRef here.
  useEffect(() => {
    const handler = (e) => {
      const newNodes = e.detail?.nodes;
      if (!Array.isArray(newNodes)) return;
      const prev = prevNodesRef.current;
      checkTriggers(prev, newNodes);
      // Sync refs so a subsequent inbox write diffs against the latest state
      prevNodesRef.current = newNodes;
      nodesRef.current = newNodes;
      // Update React state so the canvas reflects the Firestore write
      setNodes(newNodes);
    };
    window.addEventListener('projectly_collection_changed', handler);
    return () => window.removeEventListener('projectly_collection_changed', handler);
  }, [checkTriggers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toggle pipeline active (persists to Firestore + manages active_pipelines) ─
  const togglePipelineActive = useCallback(() => {
    setPipelineActive(prev => {
      const next = !prev;
      dataService.setPipelineActive(workspaceId, next, nodesRef.current).catch(e =>
        console.error('[togglePipelineActive]', e)
      );
      return next;
    });
  }, [workspaceId]);

  // ── Schedule & poll_api interval triggers ─────────────────────────────────
  const scheduleTimersRef = useRef({});
  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      Object.values(scheduleTimersRef.current).forEach(clearInterval);
      scheduleTimersRef.current = {};
    };
  }, []);
  useEffect(() => {
    if (!pipelineActive) {
      Object.values(scheduleTimersRef.current).forEach(clearInterval);
      scheduleTimersRef.current = {};
      return;
    }
    const currentNodes = nodesRef.current;
    const timedTriggers = currentNodes.filter(n =>
      n.type === 'triggerNode' && (n.data.triggerType === 'schedule' || n.data.triggerType === 'poll_api')
    );
    const activeIds = new Set(timedTriggers.map(t => t.id));
    Object.keys(scheduleTimersRef.current).forEach(id => {
      if (!activeIds.has(id)) { clearInterval(scheduleTimersRef.current[id]); delete scheduleTimersRef.current[id]; }
    });
    timedTriggers.forEach(trigger => {
      if (scheduleTimersRef.current[trigger.id]) return;
      const intervalSec = trigger.data.triggerType === 'schedule'
        ? parseInt(trigger.data.scheduleInterval || '60', 10)
        : parseInt(trigger.data.pollInterval || '300', 10);
      const ms = Math.max(intervalSec * 1000, 10000);

      if (trigger.data.triggerType === 'schedule') {
        scheduleTimersRef.current[trigger.id] = setInterval(() => {
          addLog({ type: 'workflow', nodeLabel: trigger.data?.label || 'Schedule', agentName: 'System', detail: 'Schedule trigger fired' });
          runWorkflow(trigger.id);
        }, ms);
      } else if (trigger.data.triggerType === 'poll_api') {
        const doPoll = async () => {
          try {
            const connNode = nodesRef.current.find(n => n.id === trigger.data.pollConnectionId);
            const baseUrl = connNode?.data?.serviceUrl || '';
            const endpoint = trigger.data.pollEndpoint || '';
            const url = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}` : endpoint;
            if (!url) return;
            const targetHeaders = {};
            if (connNode?.data?.authToken) targetHeaders['Authorization'] = `Bearer ${connNode.data.authToken}`;
            const resp = await fetch('/api/proxy', {
              headers: {
                'X-Proxy-Target': url,
                'X-Proxy-Headers': JSON.stringify(targetHeaders),
              },
            });
            if (!resp.ok) return;
            const data = await resp.json();
            const condField = trigger.data.pollConditionField;
            const condValue = trigger.data.pollConditionValue;
            if (condField) {
              const parts = condField.split('.');
              let val = data;
              for (const p of parts) { val = val?.[p]; }
              if (condValue && String(val).toLowerCase().trim() !== String(condValue).toLowerCase().trim()) return;
              if (!condValue && !val) return;
            }
            addLog({ type: 'workflow', nodeLabel: trigger.data?.label || 'Poll', agentName: 'System', detail: 'poll_api trigger fired' });
            runWorkflow(trigger.id);
          } catch (e) {
            console.error('[poll_api trigger]', e);
          }
        };
        scheduleTimersRef.current[trigger.id] = setInterval(doPoll, ms);
      }
    });
    // No cleanup — intervals persist across re-renders; cleared when pipelineActive
    // becomes false, triggers are removed, or on unmount (separate effect above).
  }, [pipelineActive, runWorkflow, nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteNode = useCallback((nodeId) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setNodes(nds => {
      const next = nds.filter(n => n.id !== nodeId);
      setEdges(eds => {
        const nextEdges = eds.filter(e => e.source !== nodeId && e.target !== nodeId);
        scheduleSave(next, nextEdges);
        return nextEdges;
      });
      return next;
    });
    setSelectedNode(null);
  }, [scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyGeneratedConfig = useCallback((nodeId, config) => {
    const { connectToNodeId, ...nodeConfig } = config;
    updateNodeData(nodeId, nodeConfig);
    if (connectToNodeId) {
      // Exact ID match first, then fall back to label match (AI sometimes outputs name instead of ID)
      const target = nodes.find(n => n.id === connectToNodeId)
        || nodes.find(n => (n.data?.label || '').toLowerCase() === String(connectToNodeId).toLowerCase());
      const resolvedId = target?.id || connectToNodeId;
      setEdges(eds => { // eslint-disable-line react-hooks/exhaustive-deps
        const exists = eds.some(e =>
          (e.source === resolvedId && e.target === nodeId) ||
          (e.source === nodeId    && e.target === resolvedId)
        );
        if (exists) return eds;
        const newEdge = {
          id: `e-${resolvedId}-${nodeId}`,
          source: resolvedId,
          target: nodeId,
          type: 'default',
          animated: true,
          style: { stroke: 'var(--edge-color, #94a3b8)', strokeWidth: 2 },
        };
        const next = [...eds, newEdge];
        scheduleSave(nodes, next);
        return next;
      });
    }
  }, [updateNodeData, nodes, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = useCallback((modal) => {
    const key = `${modal.nodeId}_${modal.mode}_${modal.agentName || ''}`;
    setChatWindows(ws => {
      const existing = ws.find(w => w.key === key);
      if (existing) return ws.map(w => w.key === key ? { ...w, minimized: false } : w);
      const offset = ws.filter(w => !w.minimized).length * 28;
      return [...ws, {
        key, modal, minimized: false,
        initialPos: { x: Math.max(20, window.innerWidth - 480 - offset), y: Math.max(60, window.innerHeight - 590 - offset) },
      }];
    });
  }, []);

  const handleSendToAgents = useCallback(async (collectionId, recordId) => {
    const colNode = nodes.find(n => n.id === collectionId);
    if (!colNode) return;
    const records = await dataService.getRecords(workspaceId, collectionId);
    const record = records.find(r => r.id === recordId);
    if (!record) return;
    const connectedFnIds = [...new Set(
      edges
        .filter(e => e.source === collectionId || e.target === collectionId)
        .map(e => e.source === collectionId ? e.target : e.source)
    )].filter(id => nodes.find(n => n.id === id && n.type === 'functionNode'));
    if (!connectedFnIds.length) return;
    const fnNode = nodes.find(n => n.id === connectedFnIds[0]);
    if (!fnNode) return;
    const fieldLines = (colNode.data.fields || []).map(f => `${f.name}: "${record.values?.[f.id] || ''}"`).join('\n');
    const prefillMessage = `New item from "${colNode.data.label}" (record ID: ${record.id}):\n${fieldLines}\n\nPlease process this according to your playbook.`;
    openChat({
      nodeId: fnNode.id,
      mode: 'team',
      nodeLabel: fnNode.data.label,
      playbook: fnNode.data.playbook,
      connectedCollections: getConnectedCollections(fnNode.id),
      connectedGates: getConnectedGates(fnNode.id),
      teamMembers: (fnNode.data.agents || []).map(a => ({ name: a.name, role: a.role, isManager: a.isManager, instructions: a.instructions })),
      prefillMessage,
    });
  }, [nodes, edges, openChat, getConnectedCollections, getConnectedGates, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openGenerator = useCallback((nodeId, nodeType, nodeName) => {
    const key = `gen_${nodeId}`;
    setGenWindows(ws => {
      const existing = ws.find(w => w.key === key);
      if (existing) return ws.map(w => w.key === key ? { ...w, minimized: false } : w);
      const offset = ws.filter(w => !w.minimized).length * 28;
      return [...ws, {
        key, nodeId, nodeType, nodeName, minimized: false,
        initialPos: { x: Math.max(20, window.innerWidth - 540 - offset), y: Math.max(20, window.innerHeight - 660 - offset) },
      }];
    });
  }, []);

  const handleUpdateCollection = useCallback(async (colId, op) => {
    if (op.action === 'create') {
      await dataService.addRecord(workspaceId, colId, op.data || {});
    } else if (op.action === 'update' && op.recordId) {
      await dataService.patchRecord(workspaceId, colId, op.recordId, op.data || {});
    } else if (op.action === 'delete' && op.recordId) {
      await dataService.deleteRecord(workspaceId, colId, op.recordId);
    }
  }, [workspaceId]);

  const handleApplySuggestion = useCallback((colId, suggestion) => {
    const colNode = nodes.find(n => n.id === colId);
    if (!colNode) return;
    if (suggestion.type === 'add_field') {
      const newField = {
        id: `f${Date.now()}`,
        name: suggestion.fieldName,
        type: suggestion.fieldType || 'text',
        ...(suggestion.options?.length ? { options: suggestion.options } : {}),
      };
      updateNodeData(colId, { fields: [...(colNode.data.fields || []), newField] });
    }
  }, [nodes, updateNodeData]);

  const deleteEdge = useCallback((edgeId) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setEdges(eds => {
      const next = eds.filter(e => e.id !== edgeId);
      scheduleSave(nodes, next);
      return next;
    });
  }, [nodes, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateNode = useCallback((nodeId) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setNodes(nds => {
      const src = nds.find(n => n.id === nodeId);
      if (!src) return nds;
      const cloned = {
        ...src,
        id: newId(src.type === 'noteNode' ? 'note' : src.type === 'collectionNode' ? 'col' : 'node'),
        position: { x: src.position.x + 40, y: src.position.y + 40 },
        selected: false,
      };
      const next = [...nds, cloned];
      scheduleSave(next, edges);
      setSelectedNode(cloned);
      return next;
    });
  }, [edges, scheduleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const animateWalk = useCallback((agentName, agentEmoji, fromNodeId, toNodeId) => {
    const edge = edges.find(e =>
      (e.source === fromNodeId && e.target === toNodeId) ||
      (e.source === toNodeId   && e.target === fromNodeId)
    );
    if (!edge) return;
    const isForward = edge.source === fromNodeId;
    const pathEl = document.querySelector(`[data-testid="rf__edge-${edge.id}"] .react-flow__edge-path`);
    if (!pathEl) return;

    const avatarId   = `walk_${Date.now()}`;
    const totalLen   = pathEl.getTotalLength();

    const getPos = (t) => {
      const d = isForward ? t * totalLen : (1 - t) * totalLen;
      const pt  = pathEl.getPointAtLength(d);
      const ctm = pathEl.getScreenCTM();
      if (!ctm) return null;
      return { x: ctm.a * pt.x + ctm.c * pt.y + ctm.e, y: ctm.b * pt.x + ctm.d * pt.y + ctm.f };
    };

    const start = getPos(0);
    if (!start) return;
    setWalkingAvatars(prev => [...prev, { id: avatarId, ...start, emoji: agentEmoji || '🧑', name: agentName }]);

    const DURATION = 1800;
    let startTime = null;
    let phase = 'going';

    const step = (ts) => {
      if (!startTime) startTime = ts;
      const t   = Math.min((ts - startTime) / DURATION, 1);
      const pos = phase === 'going' ? getPos(t) : getPos(1 - t);
      if (pos) setWalkingAvatars(prev => prev.map(a => a.id === avatarId ? { ...a, ...pos } : a));
      if (t < 1) {
        requestAnimationFrame(step);
      } else if (phase === 'going') {
        setTimeout(() => { phase = 'returning'; startTime = null; requestAnimationFrame(step); }, 2000);
      } else {
        setTimeout(() => setWalkingAvatars(prev => prev.filter(a => a.id !== avatarId)), 300);
      }
    };
    requestAnimationFrame(step);
  }, [edges]); // eslint-disable-line react-hooks/exhaustive-deps

  const animateFlowDot = useCallback((fromNodeId, toNodeId) => {
    const currentEdges = edgesRef.current;
    const edge = currentEdges.find(e =>
      (e.source === fromNodeId && e.target === toNodeId) ||
      (e.source === toNodeId && e.target === fromNodeId)
    );
    if (!edge) return Promise.resolve();
    const isForward = edge.source === fromNodeId;
    const pathEl = document.querySelector(`[data-testid="rf__edge-${edge.id}"] .react-flow__edge-path`);
    if (!pathEl) return Promise.resolve();

    const dotId = `dot_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const totalLen = pathEl.getTotalLength();

    const rfContainer = document.querySelector('.react-flow');
    const containerRect = rfContainer?.getBoundingClientRect();

    const getPos = (t) => {
      const d = isForward ? t * totalLen : (1 - t) * totalLen;
      const pt = pathEl.getPointAtLength(d);
      const ctm = pathEl.getScreenCTM();
      if (!ctm || !containerRect) return null;
      const screenX = ctm.a * pt.x + ctm.c * pt.y + ctm.e;
      const screenY = ctm.b * pt.x + ctm.d * pt.y + ctm.f;
      return { x: screenX - containerRect.left, y: screenY - containerRect.top };
    };

    const start = getPos(0);
    if (!start) return Promise.resolve();
    setFlowDots(prev => [...prev, { id: dotId, ...start }]);

    return new Promise(resolve => {
      const DURATION = 1500;
      let startTime = null;
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const linear = Math.min((ts - startTime) / DURATION, 1);
        const t = linear < 0.5
          ? 2 * linear * linear
          : 1 - Math.pow(-2 * linear + 2, 2) / 2;
        const pos = getPos(t);
        if (pos) setFlowDots(prev => prev.map(d => d.id === dotId ? { ...d, ...pos } : d));
        if (linear < 1) {
          requestAnimationFrame(step);
        } else {
          setTimeout(() => {
            setFlowDots(prev => prev.filter(d => d.id !== dotId));
            resolve();
          }, 150);
        }
      };
      requestAnimationFrame(step);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addNode = useCallback((type, position) => {
    const defaults = opsMode ? {
      functionNode:   { label: 'New Department',  description: '', agents: [], tasks: [], autoAssign: 'manual', taskFields: [
        { id: 'tf1', name: 'Title', type: 'text', required: true },
        { id: 'tf2', name: 'Status', type: 'select', options: ['To do', 'In Progress', 'Review', 'Done'], defaultValue: 'To do' },
        { id: 'tf3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
        { id: 'tf4', name: 'Assignee', type: 'agent' },
      ] },
      humanNode:      { label: 'Stakeholder',     description: '' },
      gateNode:       { label: 'Sign-off Point',  description: '' },
      connectionNode: { label: 'New App',         description: '' },
      noteNode:       { title: '', body: 'Add your memo here…', color: '#fef9c3', assignedLabels: [] },
      collectionNode: {
        label: 'New Records', description: '',
        fields: [
          { id: 'f1', name: 'Title',  type: 'text' },
          { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'In Progress', 'Done'] },
        ],
        records: []
      },
      triggerNode:    { label: 'Automation Start', description: '', triggerType: 'manual' },
      conditionNode:  { label: 'Decision Point',   description: '', conditions: [], logic: 'and' },
      actionNode:     { label: 'Auto Step',        description: '', actionType: 'update_field' },
    } : {
      functionNode:   { label: 'New Function',   description: '', agents: [], tasks: [], autoAssign: 'manual', taskFields: [
        { id: 'tf1', name: 'Title', type: 'text', required: true },
        { id: 'tf2', name: 'Status', type: 'select', options: ['To do', 'In Progress', 'Review', 'Done'], defaultValue: 'To do' },
        { id: 'tf3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
        { id: 'tf4', name: 'Assignee', type: 'agent' },
      ] },
      humanNode:      { label: 'Human Input',    description: '' },
      gateNode:       { label: 'Approval Gate',  description: '' },
      connectionNode: { label: 'New Connection', description: '' },
      noteNode:       { title: '', body: 'Add your note here…', color: '#fef9c3', assignedLabels: [] },
      collectionNode: {
        label: 'New Collection', description: '',
        fields: [
          { id: 'f1', name: 'Title',  type: 'text' },
          { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'In Progress', 'Done'] },
        ],
        records: []
      },
      triggerNode:    { label: 'New Trigger',   description: '', triggerType: 'manual' },
      conditionNode:  { label: 'New Condition', description: '', conditions: [], logic: 'and' },
      actionNode:     { label: 'New Action',    description: '', actionType: 'update_field' },
    };
    const pos = position || { x: 200 + Math.random() * 300, y: 200 + Math.random() * 200 };
    const newNode = { id: newId(type === 'noteNode' ? 'note' : type === 'collectionNode' ? 'col' : 'node'), type, position: pos, data: defaults[type] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setNodes(nds => {
      const next = [...nds, newNode];
      scheduleSave(next, edges);
      return next;
    });
    setSelectedNode(newNode);
  }, [edges, scheduleSave, opsMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Context menu ────────────────────────────────────────────────────────────
  const openContextMenu = useCallback((cm) => setContextMenu(cm), []);

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    setContextMenu({ type: node.type, nodeId: node.id, x: e.clientX, y: e.clientY });
  }, []);

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault();
    const rect     = e.currentTarget.getBoundingClientRect();
    const panePos  = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setContextMenu({ type: 'pane', x: e.clientX, y: e.clientY, panePos });
  }, []);

  const onEdgeContextMenu = useCallback((e, edge) => {
    e.preventDefault();
    setContextMenu({ type: 'edge', edgeId: edge.id, x: e.clientX, y: e.clientY });
  }, []);

  const addNodeAt = useCallback((type, cm) => {
    const pos = rfInstance.current
      ? rfInstance.current.project(cm.panePos)
      : { x: 200 + Math.random() * 300, y: 200 + Math.random() * 200 };
    addNode(type, pos);
  }, [addNode]);

  const buildMenuItems = useCallback((cm) => {
    if (!cm) return [];
    const { type, nodeId, agent } = cm;
    const node = nodes.find(n => n.id === nodeId);

    if (type === 'edge') {
      return [
        { icon: '🗑️', label: 'Remove connection', danger: true, onClick: () => { deleteEdge(cm.edgeId); setContextMenu(null); } },
      ];
    }

    if (type === 'pane') {
      return opsMode ? [
        { icon: '🏢', label: 'Add Department',      onClick: () => addNodeAt('functionNode', cm) },
        { icon: '👥', label: 'Add Stakeholder',     onClick: () => addNodeAt('humanNode',      cm) },
        { icon: '✅', label: 'Add Approval',        onClick: () => addNodeAt('gateNode',       cm) },
        { icon: '📌', label: 'Add Memo',            onClick: () => addNodeAt('noteNode',       cm) },
        { icon: '📁', label: 'Add Records',         onClick: () => addNodeAt('collectionNode', cm) },
        { divider: true },
        { icon: '🚀', label: 'Add Automation Start', onClick: () => addNodeAt('triggerNode',   cm) },
        { icon: '🔀', label: 'Add Decision Point',   onClick: () => addNodeAt('conditionNode', cm) },
        { icon: '⚙',  label: 'Add Auto Step',        onClick: () => addNodeAt('actionNode',    cm) },
        { divider: true },
        { icon: '🔌', label: 'Add App', soon: true },
        { divider: true },
        { icon: '⏱', label: 'Pipeline schedule', onClick: () => { setShowExecution(true); setSelectedNode(null); setContextMenu(null); } },
      ] : [
        { icon: '⬡', label: 'Add Function Node',   onClick: () => addNodeAt('functionNode', cm) },
        { icon: '👤', label: 'Add Human Node',      onClick: () => addNodeAt('humanNode',      cm) },
        { icon: '◆',  label: 'Add Gate Node',       onClick: () => addNodeAt('gateNode',       cm) },
        { icon: '📝', label: 'Add Note',            onClick: () => addNodeAt('noteNode',       cm) },
        { icon: '⊞',  label: 'Add Collection',      onClick: () => addNodeAt('collectionNode', cm) },
        { divider: true },
        { icon: '⚡', label: 'Add Trigger Node',    onClick: () => addNodeAt('triggerNode',    cm) },
        { icon: '◇',  label: 'Add Condition Node',  onClick: () => addNodeAt('conditionNode',  cm) },
        { icon: '⚙',  label: 'Add Action Node',     onClick: () => addNodeAt('actionNode',     cm) },
        { divider: true },
        { icon: '🔌', label: 'Add Connection', soon: true },
        { divider: true },
        { icon: '⏱', label: 'Pipeline schedule', onClick: () => { setShowExecution(true); setSelectedNode(null); setContextMenu(null); } },
      ];
    }

    if (type === 'agent' && agent) {
      const currentModel = agent.model || 'standard';
      const setModel = (m) => {
        const agents = (node?.data.agents || []).map(a =>
          a.id === agent.id ? { ...a, model: m } : a
        );
        updateNodeData(nodeId, { agents });
      };

      // Escalation options (ops mode only) — connected function nodes
      const escalateItems = [];
      if (opsMode) {
        const connectedDepts = edges
          .filter(e => e.source === nodeId)
          .map(e => nodes.find(n => n.id === e.target))
          .filter(n => n?.type === 'functionNode');
        if (connectedDepts.length > 0) {
          escalateItems.push({ type: 'section', label: 'Escalate to' });
          connectedDepts.forEach(dept => {
            escalateItems.push({
              icon: '👤',
              label: dept.data.label,
              onClick: () => { animateWalk(agent.name, agent.emoji, nodeId, dept.id); setContextMenu(null); },
            });
          });
          escalateItems.push({ divider: true });
        }
      }

      return [
        { icon: '✏️', label: `Rename "${agent.name}"`, onClick: () => { setSelectedNode(node); } },
        {
          icon: agent.isManager ? '↓' : '⭐',
          label: agent.isManager
            ? (opsMode ? 'Remove head role' : 'Remove manager role')
            : (opsMode ? 'Make Department Head' : 'Make manager'),
          onClick: () => {
            const agents = (node?.data.agents || []).map(a =>
              a.id === agent.id ? { ...a, isManager: !a.isManager } : a
            );
            updateNodeData(nodeId, { agents });
          }
        },
        { divider: true },
        ...escalateItems,
        { type: 'section', label: 'Intelligence' },
        { icon: '⚡', label: `${currentModel === 'fast'     ? '✓ ' : '  '}Efficient (Haiku 4.5)`, onClick: () => setModel('fast') },
        { icon: '◈', label: `${currentModel === 'standard' ? '✓ ' : '  '}Balanced (Sonnet 4.6)`,  onClick: () => setModel('standard') },
        { icon: '★', label: `${currentModel === 'smart'    ? '✓ ' : '  '}Expert (Opus 4.8)`,       onClick: () => setModel('smart') },
        { divider: true },
        {
          icon: agent.paused ? '▶' : '⏸',
          label: agent.paused
            ? (opsMode ? 'Back from leave' : 'Resume agent')
            : (opsMode ? 'Send on leave 🏖️' : 'Pause agent'),
          onClick: () => {
            const agents = (node?.data.agents || []).map(a =>
              a.id === agent.id ? { ...a, paused: !a.paused } : a
            );
            updateNodeData(nodeId, { agents });
          }
        },
        { divider: true },
        { icon: '💬', label: opsMode ? `Message ${agent.name}` : `Chat with ${agent.name}`, onClick: () => { openChat({ nodeId, agentName: agent.name, agentRole: agent.role, agentInstructions: agent.instructions, nodeLabel: node?.data.label, playbook: node?.data.playbook, mode: 'agent', connectedCollections: getConnectedCollections(nodeId), connectedGates: getConnectedGates(nodeId), teamMembers: (node?.data.agents || []).filter(a => a.id !== agent.id).map(a => ({ name: a.name, role: a.role, isManager: a.isManager })) }); setContextMenu(null); } },
        { icon: '📋', label: 'View tasks', onClick: () => { setTasksModal({ nodeId, nodeLabel: node?.data.label || 'Node', tasks: node?.data.tasks || [] }); setContextMenu(null); } },
        { divider: true },
        {
          icon: '✕', label: opsMode ? 'Remove employee' : 'Remove agent', danger: true,
          onClick: () => {
            const agents = (node?.data.agents || []).filter(a => a.id !== agent.id);
            updateNodeData(nodeId, { agents });
          }
        },
      ];
    }

    const editItem      = { icon: '✏️', label: 'Edit', onClick: () => setSelectedNode(node) };
    const duplicateItem = { icon: '⧉', label: 'Duplicate', onClick: () => duplicateNode(nodeId) };
    const nodeLabel = opsMode ? ({
      functionNode: 'department', humanNode: 'stakeholder', gateNode: 'approval',
      connectionNode: 'app', noteNode: 'memo', collectionNode: 'records',
    }[type] || 'item') : 'node';
    const deleteItem    = { icon: '🗑️', label: `Delete ${nodeLabel}`, danger: true, onClick: () => deleteNode(nodeId) };
    const colorPalette = {
      type: 'palette',
      label: 'Color',
      colors: type === 'noteNode' ? NOTE_COLORS : NODE_COLORS,
      active: node?.data.color,
      onSelect: (c) => updateNodeData(nodeId, { color: c }),
    };

    switch (type) {
      case 'functionNode': return [
        editItem,
        { icon: '📋', label: opsMode ? 'View work items' : 'View tasks', onClick: () => { setFunctionTaskView(nodeId); setContextMenu(null); } },
        duplicateItem, colorPalette, { divider: true },
        ...(aiEnabled ? [
          { icon: '✨', label: 'Generate with AI', onClick: () => { openGenerator(nodeId, 'functionNode', node?.data.label); setContextMenu(null); } },
          { icon: '💬', label: opsMode ? 'Chat with team' : 'Chat with agents', onClick: () => { openChat({ nodeId, mode: 'team', nodeLabel: node?.data.label, playbook: node?.data.playbook, connectedCollections: getConnectedCollections(nodeId), connectedGates: getConnectedGates(nodeId), teamMembers: (node?.data.agents || []).map(a => ({ name: a.name, role: a.role, isManager: a.isManager, instructions: a.instructions })) }); setContextMenu(null); } },
        ] : []),
        { icon: '⏱', label: 'Edit schedule', onClick: () => { setSelectedNode(node); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      case 'humanNode': return [
        editItem, duplicateItem, colorPalette, { divider: true },
        ...(aiEnabled ? [
          { icon: '💬', label: 'Direct message', onClick: () => { openChat({ nodeId, mode: 'direct', nodeLabel: node?.data.label }); setContextMenu(null); } },
        ] : []),
        { icon: '📋', label: opsMode ? 'Raise task' : 'View tasks', onClick: () => { setTasksModal({ nodeId, nodeLabel: node?.data.label || 'Node', tasks: node?.data.tasks || [], raiseMode: true }); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      case 'gateNode': return [
        editItem, duplicateItem, colorPalette, { divider: true },
        ...(aiEnabled ? [
          { icon: '✨', label: 'Generate with AI', onClick: () => { openGenerator(nodeId, 'gateNode', node?.data.label); setContextMenu(null); } },
        ] : []),
        { icon: '⚙️', label: 'Configure rules', onClick: () => { setSelectedNode(node); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      case 'connectionNode': return [
        editItem, duplicateItem, colorPalette, { divider: true },
        { icon: '🔗', label: 'Configure connection', onClick: () => { setSelectedNode(node); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      case 'noteNode': return [
        editItem, duplicateItem, colorPalette, { divider: true },
        { icon: '📌', label: node?.data.pinned ? 'Unpin note' : 'Pin note', onClick: () => { updateNodeData(nodeId, { pinned: !node?.data.pinned }); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      case 'collectionNode': return [
        { icon: '✏️', label: 'Open editor', onClick: () => { setCollectionModal(nodeId); setContextMenu(null); } },
        duplicateItem, colorPalette, { divider: true },
        ...(aiEnabled ? [
          { icon: '✨', label: 'Generate with AI', onClick: () => { openGenerator(nodeId, 'collectionNode', node?.data.label); setContextMenu(null); } },
        ] : []),
        { icon: '➕', label: 'Add record', onClick: () => { setCollectionModal(nodeId); setContextMenu(null); } },
        { divider: true }, deleteItem,
      ];
      default: return [editItem, { divider: true }, deleteItem];
    }
  }, [nodes, edges, addNodeAt, updateNodeData, deleteNode, deleteEdge, duplicateNode, opsMode, animateWalk, openChat, openGenerator]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="pipeline-loading">Loading pipeline…</div>;

  if (showStarter) return (
    <div className="pipeline-starter">
      <div className="pipeline-starter-box">
        <div className="pipeline-starter-icon">⬡</div>
        <h2 className="pipeline-starter-title">Start your pipeline</h2>
        <p className="pipeline-starter-sub">Choose how you'd like to begin.</p>
        <div className="pipeline-starter-options">
          <button className="pipeline-starter-opt" onClick={() => handleStarterChoice(false)}>
            <span className="pipeline-starter-opt-icon">✦</span>
            <span className="pipeline-starter-opt-label">Blank canvas</span>
            <span className="pipeline-starter-opt-desc">Start from scratch — add your own nodes and connections.</span>
          </button>
          <button className="pipeline-starter-opt" onClick={() => handleStarterChoice(true)}>
            <span className="pipeline-starter-opt-icon">◈</span>
            <span className="pipeline-starter-opt-label">Example pipeline</span>
            <span className="pipeline-starter-opt-desc">Load a pre-built marketing &amp; support pipeline to explore the platform.</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <PipelineCtx.Provider value={{ openContextMenu, showTokens, tokenData, fmtTokens, fmtCost, opsMode, pipelineActive, onRunWorkflow: runWorkflow, workspaceId, canEdit, hasCapability, nodes, edges }}>
      <div className={`pipeline-container${opsMode ? ' ops-mode' : ''}`}>
        <div className="pipeline-toolbar">
          {canEdit && (<>
          <span className="pipeline-toolbar-label">Add:</span>
          {opsMode ? (<>
            <button className="add-node-btn function-btn"   onClick={() => addNode('functionNode')}>🏢 Department</button>
            <button className="add-node-btn human-btn"      onClick={() => addNode('humanNode')}>👥 Stakeholder</button>
            <button className="add-node-btn gate-btn"       onClick={() => addNode('gateNode')}>✅ Approval</button>
            <button className="add-node-btn note-btn"       onClick={() => addNode('noteNode')}>📌 Memo</button>
            <button className="add-node-btn collection-btn" onClick={() => addNode('collectionNode')}>📁 Records</button>
            <button className="add-node-btn connection-btn" onClick={() => addNode('connectionNode')}>🔌 App</button>
            <span className="toolbar-divider" />
            <button className="add-node-btn trigger-btn"   onClick={() => addNode('triggerNode')}>🚀 Automation Start</button>
            <button className="add-node-btn condition-btn" onClick={() => addNode('conditionNode')}>🔀 Decision</button>
            <button className="add-node-btn action-btn"    onClick={() => addNode('actionNode')}>⚙ Auto Step</button>
          </>) : (<>
            <button className="add-node-btn function-btn"   onClick={() => addNode('functionNode')}>⬡ Function</button>
            <button className="add-node-btn human-btn"      onClick={() => addNode('humanNode')}>👤 Human</button>
            <button className="add-node-btn gate-btn"       onClick={() => addNode('gateNode')}>◆ Gate</button>
            <button className="add-node-btn note-btn"       onClick={() => addNode('noteNode')}>📝 Note</button>
            <button className="add-node-btn collection-btn" onClick={() => addNode('collectionNode')}>⊞ Collection</button>
            <button className="add-node-btn connection-btn" onClick={() => addNode('connectionNode')}>⚡ Connection</button>
            <span className="toolbar-divider" />
            <button className="add-node-btn trigger-btn"   onClick={() => addNode('triggerNode')}>⚡ Trigger</button>
            <button className="add-node-btn condition-btn" onClick={() => addNode('conditionNode')}>◇ Condition</button>
            <button className="add-node-btn action-btn"    onClick={() => addNode('actionNode')}>⚙ Action</button>
          </>)}
          </>)}
          <div className="toolbar-spacer" />
          {aiEnabled && (
            <button
              className={`token-toggle-btn ${showTokens ? 'active' : ''}`}
              onClick={() => setShowTokens(t => !t)}
              title={opsMode ? 'Toggle cost overlay ($ per agent)' : 'Toggle token usage overlay'}
            >
              {opsMode ? '💰' : '📊'} {showTokens ? (opsMode ? 'Hide costs' : 'Hide tokens') : (opsMode ? 'Show costs' : 'Token usage')}
            </button>
          )}
          <button
            className={`play-btn${pipelineActive ? ' active' : ''}`}
            onClick={togglePipelineActive}
            title={pipelineActive ? 'Stop pipeline' : 'Run pipeline'}
          >
            {pipelineActive ? '⏹ Stop' : '▶ Run'}
          </button>
          <button
            className={`exec-schedule-btn${showExecution ? ' active' : ''}`}
            onClick={() => { setShowExecution(e => !e); setSelectedNode(null); }}
            title="Execution & Schedules"
          >
            ⏱ Schedule
          </button>
          <span className={`autosave-indicator ${saveState}`}>
            {saveState === 'saving' ? '⟳ Saving…' : '✓ Saved'}
          </span>
        </div>

        <div className="pipeline-canvas-wrap" style={{ flex: 1, display: 'flex' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={canEdit ? handleNodesChange : undefined}
              onEdgesChange={canEdit ? handleEdgesChange : undefined}
              onConnect={canEdit ? onConnect : undefined}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onNodeContextMenu={canEdit ? onNodeContextMenu : undefined}
              onEdgeContextMenu={canEdit ? onEdgeContextMenu : undefined}
              onPaneContextMenu={canEdit ? onPaneContextMenu : undefined}
              onInit={(inst) => { rfInstance.current = inst; }}
              nodeTypes={NODE_TYPES}
              nodesDraggable={canEdit}
              nodesConnectable={canEdit}
              elementsSelectable={canEdit}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              deleteKeyCode={canEdit ? "Delete" : null}
            >
              <Background color="#e2e8f0" gap={20} />
              <Controls />
              <MiniMap
                nodeColor={(n) => ({
                  functionNode:   '#818cf8',
                  humanNode:      '#34d399',
                  gateNode:       '#fb923c',
                  connectionNode: '#94a3b8',
                  noteNode:       '#fbbf24',
                  collectionNode: '#2dd4bf',
                }[n.type] || '#94a3b8')}
                style={{ background: 'var(--bg-secondary)' }}
              />
            </ReactFlow>
            {flowDots.map(dot => (
              <div
                key={dot.id}
                className="flow-dot"
                style={{ left: dot.x, top: dot.y }}
              />
            ))}
          </div>

          {selectedNode && !showExecution && (
            <NodePanel
              node={selectedNode}
              allNodes={nodes}
              onUpdate={updateNodeData}
              onDelete={deleteNode}
              onClose={() => setSelectedNode(null)}
              opsMode={opsMode}
              aiEnabled={aiEnabled}
              onOpenGenerator={aiEnabled ? (nodeId, nodeType, nodeName) => {
                setSelectedNode(null);
                openGenerator(nodeId, nodeType, nodeName);
              } : undefined}
              onGateApprove={handleGateApprove}
              onSendToAgents={handleSendToAgents}
              onOpenTaskView={(nodeId) => { setSelectedNode(null); setFunctionTaskView(nodeId); }}
            />
          )}
          {showExecution && (
            <ExecutionPanel
              nodes={nodes}
              pipelineActive={pipelineActive}
              pipelineSchedule={pipelineSchedule}
              onTogglePipeline={togglePipelineActive}
              onSetPipelineSchedule={setPipelineSchedule}
              onUpdateNode={updateNodeData}
              onClose={() => setShowExecution(false)}
              opsMode={opsMode}
            />
          )}
        </div>
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={buildMenuItems(contextMenu)}
          onClose={() => setContextMenu(null)}
        />
      )}
      {walkingAvatars.map(av => (
        <div
          key={av.id}
          className="ops-walking-avatar"
          style={{ left: av.x, top: av.y }}
          title={av.name}
        >
          {av.emoji}
        </div>
      ))}
      {chatWindows.filter(w => !w.minimized).map(w => (
        <ChatModal
          key={w.key}
          chatKey={w.key}
          modal={w.modal}
          initialPos={w.initialPos}
          workspaceId={workspaceId}
          onClose={() => setChatWindows(ws => ws.filter(x => x.key !== w.key))}
          onMinimize={() => setChatWindows(ws => ws.map(x => x.key === w.key ? { ...x, minimized: true } : x))}
          onUpdateCollection={handleUpdateCollection}
          onApplySuggestion={handleApplySuggestion}
          onGateRequest={handleGateRequest}
        />
      ))}
      {tasksModal && <TasksModal modal={tasksModal} onClose={() => setTasksModal(null)} onSaveTasks={(tasks) => updateNodeData(tasksModal.nodeId, { tasks })} />}
      {functionTaskView && (() => {
        const ftvNode = nodes.find(n => n.id === functionTaskView);
        return ftvNode ? (
          <FunctionTaskView
            node={ftvNode}
            allNodes={nodes}
            onUpdate={updateNodeData}
            onClose={() => setFunctionTaskView(null)}
            opsMode={opsMode}
          />
        ) : null;
      })()}
      {collectionModal && (
        <CollectionModal
          nodeId={collectionModal}
          allNodes={nodes}
          onUpdate={updateNodeData}
          onClose={() => setCollectionModal(null)}
          opsMode={opsMode}
          onSendToAgents={handleSendToAgents}
        />
      )}
      {aiEnabled && genWindows.filter(w => !w.minimized).map(w => (
        <GeneratorModal
          key={w.key}
          nodeId={w.nodeId}
          nodeType={w.nodeType}
          nodeName={w.nodeName}
          pipelineContext={{ nodes, edges }}
          workspaceId={workspaceId}
          onApply={applyGeneratedConfig}
          onClose={() => setGenWindows(ws => ws.filter(x => x.key !== w.key))}
          onMinimize={() => setGenWindows(ws => ws.map(x => x.key === w.key ? { ...x, minimized: true } : x))}
          initialPos={w.initialPos}
        />
      ))}
      {[...chatWindows, ...genWindows].some(w => w.minimized) && (
        <div className="float-min-bar">
          {chatWindows.filter(w => w.minimized).map(w => (
            <button key={w.key} className="float-min-chip"
              onClick={() => setChatWindows(ws => ws.map(x => x.key === w.key ? { ...x, minimized: false } : x))}>
              💬 {w.modal.agentName || w.modal.nodeLabel}
            </button>
          ))}
          {genWindows.filter(w => w.minimized).map(w => (
            <button key={w.key} className="float-min-chip gen"
              onClick={() => setGenWindows(ws => ws.map(x => x.key === w.key ? { ...x, minimized: false } : x))}>
              ✨ {w.nodeName || 'Generator'}
            </button>
          ))}
        </div>
      )}
    </PipelineCtx.Provider>
  );
}
